import { FC, ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { CenteredSpinner } from 'src/components/Spinner'
import { getAppFeatureBlockedRedirect } from 'src/helpers/appFeatureRedirect'
import { UseAppFeatureOptions, useAppFeature } from 'src/hooks/useAppFeature'
import { useUserCtx } from 'src/hooks/context-hooks'

type Props = {
  /** Stable integration id (preferred). */
  integrationKey?: string
  /** Legacy: direct feature key. Prefer integrationKey. */
  featureKey?: string
  children: ReactNode
  /** Where to send the user when blocked; defaults to first available main portal. */
  redirectTo?: string
} & UseAppFeatureOptions

/**
 * Generic UI gate: wrap routes or pages. Toggle on/off via super-admin without feature-specific guards.
 * In client admin (`/customer/:id/manage`) uses per-customer assignments automatically (scope auto).
 */
export const AppFeatureGate: FC<Props> = ({
  integrationKey,
  featureKey = '',
  children,
  redirectTo,
  accessField,
  scope,
  customerId,
}) => {
  const { allowed, isLoading, customerId: resolvedCustomerId } = useAppFeature(featureKey, {
    accessField,
    scope,
    customerId,
    integrationKey,
  })
  const { user } = useUserCtx()

  if (isLoading) {
    return <CenteredSpinner />
  }

  if (!allowed) {
    return (
      <Navigate
        to={getAppFeatureBlockedRedirect(user, resolvedCustomerId, redirectTo)}
        replace
      />
    )
  }

  return <>{children}</>
}
